import {
  buildCarePlanGeniePreview,
  CarePlanGenieFieldDefinition,
  CarePlanGenieQuestion,
} from "./carePlanGenieShared";

export interface PsychSocialGenieSectionValues {
  [key: string]: any;
}

type PsychToggleSectionConfig = {
  id: string;
  title: string;
  prompt: string;
  toggleField: string;
  detailField: string;
  assistanceField: string;
  optionLabel: string;
  optionDescription: string;
  detailText: string;
  assistanceText: string;
  detailContext?: string;
  interventionContext?: string;
};

type PsychContactSectionConfig = PsychToggleSectionConfig & {
  extraField: string;
  extraLabel: string;
  extraText: string;
};

const createPsychToggleQuestion = (config: PsychToggleSectionConfig): CarePlanGenieQuestion => ({
  id: config.id,
  title: config.title,
  prompt: config.prompt,
  helperText: "Choose details and caregiver interventions that fit best. Genie will append them into the section notes.",
  editableFieldKeys: [config.detailField, config.assistanceField],
  options: [
    {
      id: `${config.id}_yes`,
      label: config.optionLabel,
      description: config.optionDescription,
      groupLabel: "Psychosocial details",
      updates: {
        [config.toggleField]: true,
        [config.detailField]: config.detailText,
      },
    },
    {
      id: `${config.id}_pattern`,
      label: "Symptoms may vary by stress, fatigue, or routine changes",
      description: "Helpful when the issue is not constant and caregiver should watch for patterns.",
      groupLabel: "Psychosocial details",
      updates: {
        [config.toggleField]: true,
        [config.detailField]: `${config.title} concerns may increase during stress, fatigue, changes in routine, illness, or overstimulation and should be monitored for patterns.`,
      },
    },
    {
      id: `${config.id}_impact`,
      label: "Affects participation, safety, or daily routine",
      description: "Use when the concern changes how the resident participates in care or activities.",
      groupLabel: "Psychosocial details",
      updates: {
        [config.toggleField]: true,
        [config.detailField]: `${config.title} concerns can affect participation, comfort, safety awareness, or the ability to complete daily routines without added support.`,
      },
    },
    {
      id: `${config.id}_trigger_detail`,
      label: "Triggers or stressors should be documented",
      description: "Helpful when symptoms increase with specific environments, people, routines, or unmet needs.",
      groupLabel: "Psychosocial details",
      updates: {
        [config.toggleField]: true,
        [config.detailField]: `${config.title} concerns may increase with specific triggers such as overstimulation, fatigue, pain, unmet needs, unfamiliar situations, or changes in routine and should be documented clearly.`,
      },
    },
    {
      id: `${config.id}_communication_detail`,
      label: "Support approach should be clear to all caregiver",
      description: "Use when caregiver consistency makes a noticeable difference.",
      groupLabel: "Psychosocial details",
      updates: {
        [config.toggleField]: true,
        [config.detailField]: `${config.title} concerns respond best when caregiver use a consistent, calm, and predictable approach across shifts and routines.`,
      },
    },
    {
      id: `${config.id}_support`,
      label: "Needs calm and consistent support approach",
      description: "Highlights that a predictable caregiver approach improves outcomes.",
      groupLabel: "Caregiver interventions",
      updates: {
        [config.toggleField]: true,
        [config.assistanceField]: config.assistanceText,
      },
    },
    {
      id: `${config.id}_monitor`,
      label: "Watch for triggers and behavior patterns",
      description: "Caregiver should observe what tends to make the issue better or worse.",
      groupLabel: "Caregiver interventions",
      updates: {
        [config.toggleField]: true,
        [config.assistanceField]: `Caregiver should watch for triggers, timing, environment changes, and effective responses related to ${config.interventionContext || config.title.toLowerCase()} and share updates with the care team.`,
      },
    },
    {
      id: `${config.id}_redirect`,
      label: "Use reassurance, redirection, and simple cueing",
      description: "Helpful when the resident benefits from calm structure and step-by-step support.",
      groupLabel: "Caregiver interventions",
      updates: {
        [config.toggleField]: true,
        [config.assistanceField]: `Caregiver should use reassurance, redirection, and simple cueing to support the resident when ${config.interventionContext || config.title.toLowerCase()} concerns affect routine care or engagement.`,
      },
    },
    {
      id: `${config.id}_document`,
      label: "Document helpful responses and ineffective approaches",
      description: "Use when trial-and-error support needs to become more consistent over time.",
      groupLabel: "Caregiver interventions",
      updates: {
        [config.toggleField]: true,
        [config.assistanceField]: `Caregiver should document what helps, what worsens symptoms, and which approaches are ineffective when supporting ${config.interventionContext || config.title.toLowerCase()} concerns so the plan can be refined over time.`,
      },
    },
    {
      id: `${config.id}_escalate`,
      label: "Escalate worsening symptoms or safety changes",
      description: "Important when the concern increases in intensity, frequency, or risk.",
      groupLabel: "Caregiver interventions",
      updates: {
        [config.toggleField]: true,
        [config.assistanceField]: `Caregiver should escalate worsening symptoms, increased frequency, significant behavior changes, or new safety concerns related to ${config.interventionContext || config.title.toLowerCase()} when routine support is no longer enough.`,
      },
    },
    {
      id: `${config.id}_no`,
      label: `No ${config.title.toLowerCase()} concerns`,
      description: `Nothing additional needs to be documented for ${config.title.toLowerCase()} right now.`,
      groupLabel: "Psychosocial details",
      updates: {
        [config.toggleField]: false,
      },
    },
  ],
});

const createPsychContactQuestion = (config: PsychContactSectionConfig): CarePlanGenieQuestion => ({
  ...createPsychToggleQuestion(config),
  editableFieldKeys: [config.detailField, config.assistanceField, config.extraField],
  options: [
    {
      id: `${config.id}_yes`,
      label: config.optionLabel,
      description: config.optionDescription,
      groupLabel: "Psychosocial details",
      updates: {
        [config.toggleField]: true,
        [config.detailField]: config.detailText,
        [config.extraField]: config.extraText,
      },
    },
    {
      id: `${config.id}_contact`,
      label: "Current contact information should be easy to find",
      description: "Use when caregiver need ready access to provider or program contact details.",
      groupLabel: "Psychosocial details",
      updates: {
        [config.toggleField]: true,
        [config.extraField]: config.extraText,
      },
    },
    {
      id: `${config.id}_intervention_coordination`,
      label: "Coordinate with the outside mental health team",
      description: "Caregivers should share updates and support follow-through.",
      groupLabel: "Caregiver interventions",
      updates: {
        [config.toggleField]: true,
        [config.assistanceField]: config.assistanceText,
      },
    },
    {
      id: `${config.id}_intervention_followup`,
      label: "Monitor symptoms and follow-up needs",
      description: "Important when outside mental health support affects day-to-day care.",
      groupLabel: "Caregiver interventions",
      updates: {
        [config.toggleField]: true,
        [config.assistanceField]: "Caregiver should monitor symptoms, note changes in behavior or mood, and communicate follow-up needs to the mental health team when appropriate.",
      },
    },
    {
      id: `${config.id}_intervention_schedule`,
      label: "Support scheduling and attendance follow-through",
      description: "Helpful when appointments, counseling, or program contact depend on caregiver support.",
      groupLabel: "Caregiver interventions",
      updates: {
        [config.toggleField]: true,
        [config.assistanceField]: "Caregiver should support scheduling, reminders, transportation coordination, and follow-through related to the resident's mental health appointments or program involvement.",
      },
    },
    {
      id: `${config.id}_no`,
      label: `No ${config.title.toLowerCase()} concerns`,
      description: `Nothing additional needs to be documented for ${config.title.toLowerCase()} right now.`,
      groupLabel: "Psychosocial details",
      updates: {
        [config.toggleField]: false,
      },
    },
  ],
});

const PSYCH_TOGGLE_SECTIONS: PsychToggleSectionConfig[] = [
  {
    id: "sleep",
    title: "Sleep",
    prompt: "What should be documented about sleep disturbances?",
    toggleField: "anySleepDisturbances",
    detailField: "sleepDisturbancesDetails",
    assistanceField: "sleepDisturbancesAssistanceDetails",
    optionLabel: "Sleep can be disrupted",
    optionDescription: "Resident may wake often, reverse schedule, or need reassurance overnight.",
    detailText: "Resident may have disrupted sleep patterns and can wake during the night or have irregular rest routines.",
    assistanceText: "Caregiver should use a calm nighttime approach, reduce stimulation, and monitor patterns that affect rest and daytime function.",
  },
  {
    id: "night_time",
    title: "Night time",
    prompt: "What should be documented about night time assistance needs?",
    toggleField: "anyNightTimeAssistance",
    detailField: "nightTimeAssistanceDetails",
    assistanceField: "nightTimeAssistanceAssistanceDetails",
    optionLabel: "Needs nighttime assistance",
    optionDescription: "Cueing, supervision, toileting, or reassurance may be needed overnight.",
    detailText: "Resident may need nighttime assistance for safety, orientation, comfort, or routine care needs.",
    assistanceText: "Caregiver should provide nighttime cueing, reassurance, and help with needs such as toileting or safety checks as indicated.",
  },
  {
    id: "memory_short_term",
    title: "Memory impaired - short term",
    prompt: "What should be documented about short-term memory issues?",
    toggleField: "anyShortTermMemoryIssues",
    detailField: "shortTermMemoryIssuesDetails",
    assistanceField: "shortTermMemoryIssuesAssistanceDetails",
    optionLabel: "Short-term memory issues",
    optionDescription: "Needs reminders and repeated cueing for routine tasks.",
    detailText: "Resident has short-term memory difficulty and may forget recent instructions, events, or routine care steps.",
    assistanceText: "Caregiver should use repetition, simple instructions, and consistent cueing to support daily routines.",
  },
  {
    id: "memory_long_term",
    title: "Memory impaired - long term",
    prompt: "What should be documented about long-term memory issues?",
    toggleField: "anyLongTermMemoryIssues",
    detailField: "longTermMemoryIssuesDetails",
    assistanceField: "longTermMemoryIssuesAssistanceDetails",
    optionLabel: "Long-term memory issues",
    optionDescription: "Past history recall or familiar context may be affected.",
    detailText: "Resident may have long-term memory impairment that affects recall of past events, familiar details, or personal history.",
    assistanceText: "Caregiver should use familiar cues, simple explanation, and supportive redirection when long-term memory limitations affect care.",
  },
  {
    id: "oriented_person",
    title: "Memory impaired - orientated to person",
    prompt: "What should be documented about orientation to person or orientation support?",
    toggleField: "anyOrientedToPerson",
    detailField: "orientedToPersonDetails",
    assistanceField: "orientedToPersonAssistanceDetails",
    optionLabel: "Needs orientation support",
    optionDescription: "Benefits from reminders about person, place, time, or routine.",
    detailText: "Resident benefits from regular orientation support related to surroundings, routine, or the people involved in care.",
    assistanceText: "Caregiver should use calm reminders, familiar routines, and reassurance when the resident appears confused.",
  },
  {
    id: "decision_making",
    title: "Decision Making",
    prompt: "What should be documented about impaired decision making?",
    toggleField: "anyImpairedDecisionMaking",
    detailField: "impairedDecisionMakingDetails",
    assistanceField: "impairedDecisionMakingAssistanceDetails",
    optionLabel: "Impaired decision making",
    optionDescription: "Needs support making safe or informed choices.",
    detailText: "Resident may have impaired judgment or decision-making that affects safety, choices, or follow-through with care.",
    assistanceText: "Caregiver should provide supervision, simple choices, and redirection to support safer decision-making.",
  },
  {
    id: "disruptive_behavior",
    title: "Disruptive Behavior",
    prompt: "What should be documented about disruptive behavior?",
    toggleField: "anyDisruptiveBehavior",
    detailField: "disruptiveBehaviorDetails",
    assistanceField: "disruptiveBehaviorAssistanceDetails",
    optionLabel: "Disruptive behavior",
    optionDescription: "Resident may be noisy, agitated, or behaviorally disruptive at times.",
    detailText: "Resident may show disruptive behavior that affects routine, environment, or the ability to complete care tasks calmly.",
    assistanceText: "Caregiver should use calm redirection, reduce triggers when possible, and document patterns that lead to disruptive behavior.",
  },
  {
    id: "assaultive",
    title: "Assaultive",
    prompt: "What should be documented about assaultive behavior?",
    toggleField: "anyAssaultiveBehavior",
    detailField: "assaultiveBehaviorDetails",
    assistanceField: "assaultiveBehaviorAssistanceDetails",
    optionLabel: "Assaultive behavior",
    optionDescription: "Behavior can become threatening or unsafe toward others.",
    detailText: "Resident may show assaultive behavior during periods of distress, confusion, or escalation.",
    assistanceText: "Caregiver should use safety-focused de-escalation, avoid confrontation, and follow the behavior response plan when assaultive behavior occurs.",
  },
  {
    id: "resistive_to_care",
    title: "Resistive to Care",
    prompt: "What should be documented about resistance to care?",
    toggleField: "anyResistanceToCare",
    detailField: "resistanceToCareDetails",
    assistanceField: "resistanceToCareAssistanceDetails",
    optionLabel: "Resistance to care",
    optionDescription: "Care may be refused or difficult without a calm approach.",
    detailText: "Resident may resist personal care or routine support, especially during stress, confusion, or discomfort.",
    assistanceText: "Caregiver should approach slowly, explain each step, respect pauses, and re-approach when resistance increases.",
  },
  {
    id: "depression",
    title: "Depression",
    prompt: "What should be documented about depression or low mood?",
    toggleField: "anyDepression",
    detailField: "depressionDetails",
    assistanceField: "depressionAssistanceDetails",
    optionLabel: "Low mood or depression symptoms",
    optionDescription: "Needs encouragement, observation, and routine support.",
    detailText: "Resident may show low mood, sadness, or withdrawal that affects engagement and daily routine.",
    assistanceText: "Caregiver should provide encouragement, structured routine, and report changes in mood, appetite, or participation.",
  },
  {
    id: "anxiety",
    title: "Anxiety",
    prompt: "What should be documented about anxiety or worry?",
    toggleField: "anyAnxiety",
    detailField: "anxietyDetails",
    assistanceField: "anxietyAssistanceDetails",
    optionLabel: "Anxiety or worry",
    optionDescription: "Benefits from reassurance and predictable routines.",
    detailText: "Resident may have anxiety, worry, or distress during changes, uncertainty, or care tasks.",
    assistanceText: "Caregiver should use reassurance, calm communication, and predictable routine to reduce anxiety triggers when possible.",
  },
  {
    id: "irritability",
    title: "Irritability",
    prompt: "What should be documented about irritability or frustration?",
    toggleField: "anyIrritability",
    detailField: "irritabilityDetails",
    assistanceField: "irritabilityAssistanceDetails",
    optionLabel: "Irritability or frustration",
    optionDescription: "May need redirection and calm de-escalation support.",
    detailText: "Resident may become irritable or frustrated, especially during unmet needs, overstimulation, or difficult tasks.",
    assistanceText: "Caregiver should use calm redirection, allow space when needed, and look for possible triggers contributing to irritability.",
  },
  {
    id: "disorientation",
    title: "Disorientation",
    prompt: "What should be documented about disorientation?",
    toggleField: "anyDisorientation",
    detailField: "disorientationDetails",
    assistanceField: "disorientationAssistanceDetails",
    optionLabel: "Disorientation",
    optionDescription: "Resident may become confused about time, place, or surroundings.",
    detailText: "Resident may experience disorientation that affects awareness of time, place, surroundings, or routine.",
    assistanceText: "Caregiver should offer calm reminders, familiar structure, and reassurance when disorientation is noted.",
  },
  {
    id: "wandering",
    title: "Wandering",
    prompt: "What should be documented about wandering?",
    toggleField: "anyWandering",
    detailField: "wanderingDetails",
    assistanceField: "wanderingAssistanceDetails",
    optionLabel: "Wandering",
    optionDescription: "Resident may move unsafely without full awareness.",
    detailText: "Resident may wander and needs monitoring for safety, orientation, and environmental awareness.",
    assistanceText: "Caregiver should provide supervision, redirection, and a structured routine to reduce wandering-related risk.",
  },
  {
    id: "exit_seeking",
    title: "Exit Seeking",
    prompt: "What should be documented about exit-seeking behavior?",
    toggleField: "anyExitSeekingBehavior",
    detailField: "exitSeekingBehaviorDetails",
    assistanceField: "exitSeekingBehaviorAssistanceDetails",
    optionLabel: "Exit-seeking behavior",
    optionDescription: "Resident may attempt to leave the environment without supervision.",
    detailText: "Resident may show exit-seeking behavior when confused, anxious, or attempting to leave the environment.",
    assistanceText: "Caregiver should closely supervise, redirect calmly, and use safety measures appropriate to exit-seeking behavior.",
  },
  {
    id: "hallucinations",
    title: "Hallucinations",
    prompt: "What should be documented about hallucinations?",
    toggleField: "anyHallucinations",
    detailField: "hallucinationsDetails",
    assistanceField: "hallucinationsAssistanceDetails",
    optionLabel: "Hallucinations",
    optionDescription: "Needs calm support and observation for distress or escalation.",
    detailText: "Resident may experience hallucinations or perceptual disturbances that affect comfort, behavior, or communication.",
    assistanceText: "Caregiver should respond calmly, avoid arguing, redirect gently, and report changes in frequency or distress.",
  },
  {
    id: "delusions",
    title: "Delusions",
    prompt: "What should be documented about delusions?",
    toggleField: "anyDelusions",
    detailField: "delusionsDetails",
    assistanceField: "delusionsAssistanceDetails",
    optionLabel: "Delusions",
    optionDescription: "Fixed beliefs may affect interactions and emotional response.",
    detailText: "Resident may have delusional thoughts or fixed false beliefs that affect interactions and emotional response.",
    assistanceText: "Caregiver should avoid confrontation, offer reassurance, and redirect while monitoring impact on safety and care.",
  },
  {
    id: "verbal_aggression",
    title: "Verbal Aggression",
    prompt: "What should be documented about verbal aggression?",
    toggleField: "anyVerbalAggression",
    detailField: "verbalAggressionDetails",
    assistanceField: "verbalAggressionAssistanceDetails",
    optionLabel: "Verbal aggression",
    optionDescription: "Resident may yell, threaten, or speak aggressively.",
    detailText: "Resident may display verbal aggression during distress, frustration, or escalation.",
    assistanceText: "Caregiver should remain calm, avoid arguing, reduce triggers when possible, and follow de-escalation steps during verbal aggression.",
  },
  {
    id: "physical_aggression",
    title: "Physical Aggression",
    prompt: "What should be documented about physical aggression?",
    toggleField: "anyPhysicalAggression",
    detailField: "physicalAggressionDetails",
    assistanceField: "physicalAggressionAssistanceDetails",
    optionLabel: "Physical aggression",
    optionDescription: "Behavior can become physically unsafe.",
    detailText: "Resident may display physical aggression that creates safety concerns during care or distress.",
    assistanceText: "Caregiver should prioritize safety, use de-escalation, and follow the resident's support plan if physical aggression occurs.",
  },
  {
    id: "inappropriate_behavior",
    title: "Inappropriate Behavior",
    prompt: "What should be documented about inappropriate or unsafe behavior?",
    toggleField: "anyInappropriateBehavior",
    detailField: "inappropriateBehaviorDetails",
    assistanceField: "inappropriateBehaviorAssistanceDetails",
    optionLabel: "Inappropriate or unsafe behavior",
    optionDescription: "Behavior may be socially inappropriate or create safety concerns.",
    detailText: "Resident may display inappropriate or unsafe behavior that affects supervision needs, boundaries, or participation.",
    assistanceText: "Caregiver should redirect respectfully, maintain safety, and document patterns that help guide supportive response.",
  },
  {
    id: "suicidal",
    title: "Suicidal",
    prompt: "What should be documented about suicidal statements or behavior?",
    toggleField: "anySuicidalBehavior",
    detailField: "suicidalBehaviorDetails",
    assistanceField: "suicidalBehaviorAssistanceDetails",
    optionLabel: "Suicidal behavior concerns",
    optionDescription: "Requires immediate awareness, monitoring, and escalation planning.",
    detailText: "Resident has suicidal statements, thoughts, or behavior history that should be documented clearly for safety planning.",
    assistanceText: "Caregiver should follow safety protocols, maintain close observation when indicated, and escalate immediately for suicidal concerns.",
  },
  {
    id: "unfamiliar_situation",
    title: "Unfamiliar Situation",
    prompt: "What should be documented about difficulty in new situations?",
    toggleField: "anyDifficultyInNewSituations",
    detailField: "difficultyInNewSituationsDetails",
    assistanceField: "difficultyInNewSituationsAssistanceDetails",
    optionLabel: "Difficulty in new situations",
    optionDescription: "Changes or unfamiliar settings can increase distress.",
    detailText: "Resident may have difficulty adjusting to unfamiliar situations, new people, or changes in routine.",
    assistanceText: "Caregiver should prepare the resident for changes, use reassurance, and keep transitions as predictable as possible.",
  },
  {
    id: "disrobing",
    title: "Disrobing",
    prompt: "What should be documented about disrobing behavior?",
    toggleField: "anyDisrobing",
    detailField: "disrobingDetails",
    assistanceField: "disrobingAssistanceDetails",
    optionLabel: "Disrobing behavior",
    optionDescription: "Resident may remove clothing inappropriately or unexpectedly.",
    detailText: "Resident may disrobe inappropriately due to discomfort, confusion, habit, or behavioral symptoms.",
    assistanceText: "Caregiver should redirect respectfully, assess for triggers such as discomfort, and support privacy and safety.",
  },
  {
    id: "weeping",
    title: "Weeping/Crying",
    prompt: "What should be documented about weeping or crying behavior?",
    toggleField: "anyWeeping",
    detailField: "weepingDetails",
    assistanceField: "weepingAssistanceDetails",
    optionLabel: "Weeping or crying",
    optionDescription: "Emotional distress may show through crying episodes.",
    detailText: "Resident may have episodes of weeping or crying related to mood, distress, confusion, or unmet needs.",
    assistanceText: "Caregiver should respond with reassurance, comfort, and observation for triggers or changes in emotional state.",
  },
  {
    id: "unaware_consequences",
    title: "Unaware of Consequences",
    prompt: "What should be documented about lack of awareness of consequences?",
    toggleField: "anyUnawareOfConsequences",
    detailField: "unawareOfConsequencesDetails",
    assistanceField: "unawareOfConsequencesAssistanceDetails",
    optionLabel: "Unaware of consequences",
    optionDescription: "Resident may not fully understand risk or outcomes.",
    detailText: "Resident may be unaware of consequences related to choices, behavior, or safety situations.",
    assistanceText: "Caregiver should provide supervision, clear cueing, and simple explanation to help reduce risk when awareness is limited.",
  },
  {
    id: "unrealistic_fears",
    title: "Unrealistic fears and suspicions",
    prompt: "What should be documented about unrealistic fears or suspicious behavior?",
    toggleField: "anyUnrealisticFears",
    detailField: "unrealisticFearsDetails",
    assistanceField: "unrealisticFearsAssistanceDetails",
    optionLabel: "Unrealistic fears or suspicions",
    optionDescription: "Resident may be fearful, suspicious, or mistrustful.",
    detailText: "Resident may express unrealistic fears, suspicion, or mistrust that affects comfort, behavior, or acceptance of care.",
    assistanceText: "Caregiver should respond calmly, avoid confrontation, and use reassurance and consistency to reduce distress related to suspicious thoughts.",
  },
  {
    id: "spitting",
    title: "Spitting",
    prompt: "What should be documented about inappropriate spitting?",
    toggleField: "anyInappropriateSpitting",
    detailField: "inappropriateSpittingDetails",
    assistanceField: "inappropriateSpittingAssistanceDetails",
    optionLabel: "Inappropriate spitting",
    optionDescription: "Behavior may require redirection and hygiene support.",
    detailText: "Resident may have inappropriate spitting behavior that affects hygiene, dignity, or interaction with others.",
    assistanceText: "Caregiver should use calm redirection, reinforce hygiene, and watch for triggers contributing to spitting behavior.",
  },
  {
    id: "breaking_throwing",
    title: "Breaking/Throwing",
    prompt: "What should be documented about breaking or throwing items?",
    toggleField: "anyBreakThings",
    detailField: "breakThingsDetails",
    assistanceField: "breakThingsAssistanceDetails",
    optionLabel: "Breaking or throwing items",
    optionDescription: "Escalation may lead to unsafe handling of objects.",
    detailText: "Resident may break or throw items during agitation, frustration, or behavioral escalation.",
    assistanceText: "Caregiver should reduce access to unsafe items when needed, use de-escalation, and document patterns related to object-throwing behavior.",
  },
  {
    id: "other_issues",
    title: "Other Issues",
    prompt: "What other psychosocial or cognitive issues should be documented?",
    toggleField: "anyOtherPsychSocialCognitiveIssues",
    detailField: "otherPsychSocialCognitiveIssuesDetails",
    assistanceField: "otherPsychSocialCognitiveIssuesAssistanceDetails",
    optionLabel: "Other psychosocial or cognitive issues",
    optionDescription: "Additional concerns should be recorded here.",
    detailText: "Resident has other psychosocial or cognitive concerns that should be documented in this section for caregiver awareness.",
    assistanceText: "Caregiver should follow individualized support approaches for any additional psychosocial or cognitive concerns listed here.",
  },
  {
    id: "psychopharmacologic_meds",
    title: "Psychopharmacological medications",
    prompt: "What should be documented about psychopharmacological medications?",
    toggleField: "anyPsychopharmacologicMedications",
    detailField: "psychopharmacologicMedicationsDetails",
    assistanceField: "psychopharmacologicMedicationsAssistanceDetails",
    optionLabel: "Psychopharmacological medications",
    optionDescription: "Behavioral or psychiatric medication support is relevant.",
    detailText: "Resident uses psychopharmacological medication and benefits from monitoring for effectiveness, side effects, and behavior changes.",
    assistanceText: "Caregiver should watch for medication response, side effects, and any notable change in mood or behavior and report concerns promptly.",
  },
  {
    id: "crisis_plan",
    title: "Behavioral Health Support Crisis Plan",
    prompt: "What should be documented about the behavioral health support crisis plan?",
    toggleField: "anyBehavioralHealthSupportCrisisPlan",
    detailField: "behavioralHealthSupportCrisisPlanDetails",
    assistanceField: "behavioralHealthSupportCrisisPlanAssistanceDetails",
    optionLabel: "Behavioral health crisis planning",
    optionDescription: "Escalation support or crisis response should be documented clearly.",
    detailText: "Resident benefits from a clear behavioral health support or crisis response plan when distress or escalation occurs.",
    assistanceText: "Caregiver should follow the crisis support plan, use de-escalation techniques, and notify the care team when behavior changes significantly.",
  },
  {
    id: "mhp",
    title: "Medical Health Provider (MHP)",
    prompt: "What should be documented about counseling from a mental health professional?",
    toggleField: "anyCounselingFromMentalHealthProfessional",
    detailField: "counselingFromMentalHealthProfessionalDetails",
    assistanceField: "counselingFromMentalHealthProfessionalAssistanceDetails",
    optionLabel: "Mental health professional counseling",
    optionDescription: "Counseling or outside mental health follow-up is part of care.",
    detailText: "Resident receives counseling or follow-up support from a mental health professional that should be reflected in the care plan.",
    assistanceText: "Caregiver should support scheduling, encourage participation, and share relevant updates with the mental health provider when appropriate.",
  },
  {
    id: "past_behavior",
    title: "Past behavior - related to Mental Health",
    prompt: "What should be documented about past behavior related to mental health?",
    toggleField: "anyPastBehaviors",
    detailField: "pastBehaviorsDetails",
    assistanceField: "pastBehaviorsAssistanceDetails",
    optionLabel: "Past behavior history",
    optionDescription: "Past patterns can inform current support planning.",
    detailText: "Past behavior patterns should be considered when planning safe responses and supportive approaches.",
    assistanceText: "Caregiver should review known triggers and successful past interventions when caring for the resident.",
  },
  {
    id: "expanded_services",
    title: "DSHS - Expanded Community Services (HCS Only)",
    prompt: "What should be documented about expanded community services?",
    toggleField: "anyExpandedCommunityServices",
    detailField: "expandedCommunityServicesDetails",
    assistanceField: "expandedCommunityServicesAssistanceDetails",
    optionLabel: "Expanded community services",
    optionDescription: "Outside supportive services are part of the care approach.",
    detailText: "Resident receives or may benefit from expanded community-based services to support stability and overall wellbeing.",
    assistanceText: "Caregiver should help coordinate communication and follow-up with community supports as needed.",
  },
  {
    id: "specialized_behavior_services",
    title: "DSHS - Specialized Behavior Services (HCS Only)",
    prompt: "What should be documented about specialized behavior services?",
    toggleField: "anySpecializedBehavioralPrograms",
    detailField: "specializedBehavioralProgramsDetails",
    assistanceField: "specializedBehavioralProgramsAssistanceDetails",
    optionLabel: "Specialized behavior services",
    optionDescription: "Structured behavioral supports are part of care.",
    detailText: "Resident may benefit from specialized behavioral programming or supports tailored to behavior-related needs.",
    assistanceText: "Caregiver should reinforce program recommendations and monitor resident response to behavior support strategies.",
  },
];

const PSYCH_CONTACT_SECTION: PsychContactSectionConfig = {
  id: "mental_health_program",
  title: "DSHS - Mental Health Program",
  prompt: "What should be documented about the DSHS mental health program and contact information?",
  toggleField: "anyMentalHealthProgram",
  detailField: "mentalHealthProgramDetails",
  assistanceField: "mentalHealthProgramAssistanceDetails",
  extraField: "mentalHealthProgramContactInfo",
  extraLabel: "Mental health program contact info",
  optionLabel: "Mental health program or provider support",
  optionDescription: "Active mental health treatment or contact information should be listed.",
  detailText: "Resident has mental health program involvement or provider support that should be reflected in the care plan.",
  assistanceText: "Caregiver should coordinate with the mental health team and monitor symptoms or follow-up needs.",
  extraText: "Keep current contact information for the resident's mental health provider or program in this section.",
};

const PSYCH_SPECIAL_QUESTIONS: CarePlanGenieQuestion[] = [
  {
    id: "intervention_other",
    title: "Intervention - Other",
    prompt: "What should be documented about other individualized interventions?",
    editableFieldKeys: ["otherInterventionsDetails", "otherInterventionsAssistanceDetails"],
    options: [
      {
        id: "intervention_other_yes",
        label: "Other individualized interventions",
        description: "Resident has additional strategies that caregiver should use consistently.",
        groupLabel: "Psychosocial details",
        updates: {
          anyOtherInterventions: true,
          otherInterventionsDetails: "Resident has individualized intervention strategies that should be used consistently to support comfort, behavior, and participation.",
        },
      },
      {
        id: "intervention_other_support",
        label: "Follow individualized support strategies consistently",
        description: "Use when the resident benefits from specific approaches that should not vary by caregiver.",
        groupLabel: "Caregiver interventions",
        updates: {
          anyOtherInterventions: true,
          otherInterventionsAssistanceDetails: "Caregiver should follow individualized approaches consistently, monitor effectiveness, and update the team when strategies need adjustment.",
        },
      },
      {
        id: "intervention_other_document",
        label: "Document what works and what does not",
        description: "Helpful for residents with changing or complex psychosocial needs.",
        groupLabel: "Caregiver interventions",
        updates: {
          anyOtherInterventions: true,
          otherInterventionsAssistanceDetails: "Caregiver should document helpful interventions, ineffective responses, and new triggers so the support plan can be refined over time.",
        },
      },
      {
        id: "intervention_other_no",
        label: "No other intervention concerns",
        description: "Nothing additional needs to be documented for other interventions right now.",
        updates: {
          anyOtherInterventions: false,
        },
      },
    ],
  },
  {
    id: "typical_day_routine",
    title: "Typical day routine",
    prompt: "What should be documented about the resident's typical day routine?",
    editableFieldKeys: ["normalDayRoutineDetails"],
    options: [
      {
        id: "typical_day_routine_yes",
        label: "Predictable routine helps",
        description: "Resident does better with consistent pacing and familiar structure.",
        groupLabel: "Psychosocial details",
        updates: {
          normalDayRoutineDetails: "Resident benefits from a predictable daily routine with familiar timing, cueing, and a calm approach to transitions.",
        },
      },
      {
        id: "typical_day_routine_structure",
        label: "Structured routine reduces distress",
        description: "Useful when changes in schedule increase confusion, resistance, or anxiety.",
        groupLabel: "Psychosocial details",
        updates: {
          normalDayRoutineDetails: "A structured and familiar daily pattern helps reduce confusion, anxiety, and resistance during transitions or care tasks.",
        },
      },
    ],
  },
];

const PSYCH_GENIE_FIELDS = PSYCH_TOGGLE_SECTIONS.flatMap((section) => ([
  { key: section.toggleField, label: `Any ${section.title}`, type: "boolean", applyMode: "replace" },
  { key: section.detailField, label: `${section.title} details`, type: "text", applyMode: "append" },
  { key: section.assistanceField, label: `${section.title} assistance details`, type: "text", applyMode: "append" },
]));

const PSYCH_EXTRA_FIELDS = [
  { key: PSYCH_CONTACT_SECTION.toggleField, label: `Any ${PSYCH_CONTACT_SECTION.title}`, type: "boolean", applyMode: "replace" },
  { key: PSYCH_CONTACT_SECTION.detailField, label: `${PSYCH_CONTACT_SECTION.title} details`, type: "text", applyMode: "append" },
  { key: PSYCH_CONTACT_SECTION.assistanceField, label: `${PSYCH_CONTACT_SECTION.title} assistance details`, type: "text", applyMode: "append" },
  { key: PSYCH_CONTACT_SECTION.extraField, label: PSYCH_CONTACT_SECTION.extraLabel, type: "text", applyMode: "append" },
  { key: "anyOtherInterventions", label: "Any other interventions", type: "boolean", applyMode: "replace" },
  { key: "otherInterventionsDetails", label: "Other interventions details", type: "text", applyMode: "append" },
  { key: "otherInterventionsAssistanceDetails", label: "Other interventions assistance details", type: "text", applyMode: "append" },
  { key: "normalDayRoutineDetails", label: "Typical day routine details", type: "text", applyMode: "append" },
];

export const PSYCH_SOCIAL_GENIE_FIELD_DEFINITIONS: Record<string, CarePlanGenieFieldDefinition> = [
  ...PSYCH_GENIE_FIELDS,
  ...PSYCH_EXTRA_FIELDS,
].reduce((accumulator, field) => ({
  ...accumulator,
  [field.key]: field as CarePlanGenieFieldDefinition,
}), {});

export const PSYCH_SOCIAL_GENIE_QUESTIONS: CarePlanGenieQuestion[] = [
  ...PSYCH_TOGGLE_SECTIONS.map(createPsychToggleQuestion),
  createPsychContactQuestion(PSYCH_CONTACT_SECTION),
  ...PSYCH_SPECIAL_QUESTIONS,
];

export const buildPsychSocialGeniePreview = (
  currentValues: Record<string, any>,
  selectedOptions: Record<string, string[]>,
  questionDrafts: Record<string, Record<string, string>>,
) => buildCarePlanGeniePreview(
  PSYCH_SOCIAL_GENIE_FIELD_DEFINITIONS,
  PSYCH_SOCIAL_GENIE_QUESTIONS,
  currentValues,
  selectedOptions,
  questionDrafts,
);
